import React from 'react';
import { Box, Paper, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { DreamFrequencyData } from '../../hooks/useStatistics';
import { startOfWeek, endOfWeek, startOfMonth, endOfMonth, format, isWithinInterval } from 'date-fns';
import { fr } from 'date-fns/locale';
import { useTranslation } from 'react-i18next';

interface BasicStatsProps {
  data: DreamFrequencyData[];
}

const BasicStats: React.FC<BasicStatsProps> = ({ data }) => {
  const theme = useTheme();
  const { t } = useTranslation();

  // Calcul des statistiques
  const totalDreams = data.reduce((acc, curr) => acc + curr.count, 0);

  // Calcul de la moyenne hebdomadaire (par semaine calendaire)
  const weekMap = new Map<string, number>();
  data.forEach(item => {
    const weekStart = startOfWeek(item.date, { locale: fr });
    const weekKey = format(weekStart, 'yyyy-ww');
    weekMap.set(weekKey, (weekMap.get(weekKey) || 0) + item.count);
  });
  const weeklyAverage = weekMap.size > 0 
    ? Array.from(weekMap.values()).reduce((acc, count) => acc + count, 0) / weekMap.size
    : 0;

  // Calcul de la moyenne mensuelle (par mois calendaire)
  const monthMap = new Map<string, number>();
  data.forEach(item => {
    const monthKey = format(item.date, 'yyyy-MM');
    monthMap.set(monthKey, (monthMap.get(monthKey) || 0) + item.count);
  });
  const monthlyAverage = monthMap.size > 0
    ? Array.from(monthMap.values()).reduce((acc, count) => acc + count, 0) / monthMap.size
    : 0;

  const StatCard = ({ title, value, unit }: { title: string; value: number; unit: string }) => (
    <Paper
      sx={{
        flex: 1,
        minWidth: '250px',
        p: 3,
        textAlign: 'center',
        background: 'transparent',
        borderRadius: 2,
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
        transition: 'all 0.3s ease-in-out',
        '&:hover': {
          transform: 'translateY(-5px)',
          boxShadow: '0 12px 40px rgba(0, 0, 0, 0.15)',
        }
      }}
    >
      <Typography variant="h3" sx={{ 
        color: '#68f1d1',
        fontSize: '2.5rem',
        fontWeight: 'bold',
        textShadow: '0 2px 4px rgba(0, 0, 0, 0.2)'
      }}>
        {title === t('statistics.basicStats.totalDreams') ? Math.floor(value) : value.toFixed(1)}
        <Typography component="span" sx={{ 
          fontSize: '1rem',
          color: 'rgba(255, 255, 255, 0.7)',
          ml: 1
        }}>
          {unit}
        </Typography>
      </Typography>
      <Typography variant="body1" sx={{ 
        color: 'rgba(255, 255, 255, 0.9)',
        fontSize: '1rem',
        fontWeight: 'medium'
      }}>
        {title}
      </Typography>
    </Paper>
  );

  return (
    <Box sx={{ 
      display: 'flex',
      flexWrap: 'wrap',
      gap: 2,
      width: '100%'
    }}>
      <StatCard 
        title={t('statistics.basicStats.totalDreams')}
        value={totalDreams} 
        unit={t('statistics.basicStats.dreamsUnit')}
      />
      <StatCard 
        title={t('statistics.basicStats.weeklyAverage')}
        value={weeklyAverage} 
        unit={t('statistics.basicStats.dreamsPerWeek')}
      />
      <StatCard 
        title={t('statistics.basicStats.monthlyAverage')}
        value={monthlyAverage} 
        unit={t('statistics.basicStats.dreamsPerMonth')}
      />
    </Box>
  );
};

export default BasicStats;
